import { cn, Text } from "@aurem/ui";
import { lazy, Suspense, type FC } from "react";
import { ThreadMessage } from "root/ChatPanel";
import { MessageStatusEnum } from "types";
import { ExportCard } from "../ExportCard";
import { TableResultRenderer } from "../TableResultRenderer";

const TextMarkdownRenderer = lazy(() => import("../TextMarkdownRenderer"));

const DebugInfo: FC<{
  debug: { executionTrace: string; promptEnhancements?: string | null };
}> = ({ debug }) => {
  return (
    <>
      <div className="text-neutral-500 leading-relaxed">
        <Text size="sm" weight="medium" className="inline">
          EXECUTION TRACE:
        </Text>{" "}
        <Text size="sm" className="inline">
          {debug.executionTrace}
        </Text>
      </div>
      {debug.promptEnhancements && (
        <div className="text-neutral-500 leading-relaxed">
          <Text size="sm" weight="medium" className="inline">
            PROMPT ENHANCEMENTS:
          </Text>{" "}
          <Text size="sm" className="inline">
            {debug.promptEnhancements}
          </Text>
        </div>
      )}
    </>
  );
};

export const AssistantMessageCompleted: FC<{
  message: ThreadMessage;
  variant?: "panel" | "fullscreen";
}> = ({ message, variant = "panel" }) => {
  const isDebugAllowed =
    process.env.REACT_APP_ENVIRONMENT === "local" ||
    process.env.REACT_APP_ENVIRONMENT === "qa";

  if (message.status !== MessageStatusEnum.Completed) {
    return null;
  }

  if (message.metadata?.__typename !== "AssistantMessageMetadata") {
    return (
      <div className="p-3">
        <Text size="sm" className="text-neutral-500">
          {message.content}
        </Text>
      </div>
    );
  }

  const reply = message.metadata.result;

  return (
    <div
      className={cn(
        "p-3 space-y-2",
        variant === "panel" ? "max-w-[85%]" : "max-w-[65%]",
      )}
    >
      {reply?.result?.__typename === "DatabaseExportResult" && (
        <>
          {message.exportUrl && message.exportFilename && message.exportSize ? (
            <ExportCard
              reply={reply.result}
              exportUrl={message.exportUrl}
              exportFilename={message.exportFilename}
              exportSize={message.exportSize}
              isStreaming={false}
            />
          ) : (
            <Text size="sm" className="text-neutral-500 leading-relaxed">
              {reply.result.reasoning || reply.result.summary}
            </Text>
          )}
          {isDebugAllowed && reply.result.debug && (
            <DebugInfo debug={reply.result.debug} />
          )}
        </>
      )}

      {reply?.result?.__typename === "DatabaseQueryResult" && (
        <>
          {reply.result.reasoning && (
            <Text size="sm" className="text-neutral-500 leading-relaxed ">
              {reply.result.reasoning}
            </Text>
          )}
          <TableResultRenderer
            data={reply.result.data}
            totalRows={reply.result.totalCount ?? undefined}
          />
          {isDebugAllowed && reply.result.debug && (
            <DebugInfo debug={reply.result.debug} />
          )}
        </>
      )}

      {reply?.result?.__typename === "ChatResult" && (
        <Suspense fallback={null}>
          <TextMarkdownRenderer
            content={reply.result.response}
            className="text-neutral-500"
          />
        </Suspense>
      )}

      {/* Fallback */}
      {!reply && message.content && (
        <Text size="sm" className="text-neutral-500">
          {message.content}
        </Text>
      )}
    </div>
  );
};
